import React from 'react';
import PropTypes from 'prop-types';

const formatCompact = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${Number(value || 0).toLocaleString()}`;
};

const ValueRangeBar = ({
  low,
  mid,
  high,
  benchmarkLow,
  benchmarkHigh,
  className,
}) => {
  const allValues = [low, mid, high, benchmarkLow || 0, benchmarkHigh || 0].filter((v) => v > 0);
  const minValue = (allValues.length ? Math.min(...allValues) : 0) * 0.9;
  const maxValue = (allValues.length ? Math.max(...allValues) : 1) * 1.1 || 1;

  const getPosition = (value) => ((value - minValue) / (maxValue - minValue)) * 100;

  const lowPos = getPosition(low);
  const midPos = getPosition(mid);
  const highPos = getPosition(high);
  const benchmarkLowPos = benchmarkLow ? getPosition(benchmarkLow) : null;
  const benchmarkHighPos = benchmarkHigh ? getPosition(benchmarkHigh) : null;

  return (
    <div className={`space-y-3 ${className || ''}`}>
      <div className="relative h-12 bg-[hsl(var(--muted))]/30 rounded-full">
        {benchmarkLowPos !== null && benchmarkHighPos !== null && (
          <div
            className="absolute top-0 bottom-0 bg-[hsl(var(--muted))]/50 rounded-full"
            style={{
              left: `${benchmarkLowPos}%`,
              width: `${benchmarkHighPos - benchmarkLowPos}%`,
            }}
          />
        )}
        <div
          className="absolute top-0 bottom-0 bg-[hsl(var(--succeedo-orange))]/20 rounded-full"
          style={{ left: `${lowPos}%`, width: `${highPos - lowPos}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[hsl(var(--succeedo-orange))] border-2 border-[hsl(var(--background))]"
          style={{ left: `${lowPos}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[hsl(var(--succeedo-orange))] border-2 border-[hsl(var(--background))] shadow-lg"
          style={{ left: `${midPos}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[hsl(var(--succeedo-orange))] border-2 border-[hsl(var(--background))]"
          style={{ left: `${highPos}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-[11px]">
        <div className="text-[hsl(var(--muted-foreground))]">
          Low: <span className="text-[hsl(var(--foreground))] font-medium">{formatCompact(low)}</span>
        </div>
        <div className="text-[hsl(var(--muted-foreground))]">
          Mid: <span className="text-[hsl(var(--foreground))] font-semibold">{formatCompact(mid)}</span>
        </div>
        <div className="text-[hsl(var(--muted-foreground))]">
          High: <span className="text-[hsl(var(--foreground))] font-medium">{formatCompact(high)}</span>
        </div>
      </div>

      {benchmarkLow && benchmarkHigh && (
        <div className="flex items-center gap-4 text-[11px] text-[hsl(var(--muted-foreground))] pt-2 border-t border-[hsl(var(--border))]">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[hsl(var(--succeedo-orange))]/20 border border-[hsl(var(--succeedo-orange))]/40" />
            <span>Calculated range</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[hsl(var(--muted))]/50 border border-[hsl(var(--muted))]" />
            <span>
              Benchmark range ({formatCompact(benchmarkLow)} – {formatCompact(benchmarkHigh)})
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

ValueRangeBar.propTypes = {
  low: PropTypes.number.isRequired,
  mid: PropTypes.number.isRequired,
  high: PropTypes.number.isRequired,
  benchmarkLow: PropTypes.number,
  benchmarkHigh: PropTypes.number,
  className: PropTypes.string,
};

ValueRangeBar.defaultProps = {
  benchmarkLow: null,
  benchmarkHigh: null,
  className: '',
};

export default ValueRangeBar;
